import { getConfiguration, saveConfiguration } from "../../../utils/configurationStorage/configurationStorage.js"
import { z } from "zod"
import { applyConfigurationToGanttStore, subscribeGanttStoreWithConfiguration } from "../../../utils/configurationStorage/connectGanttStore.js";

/**
 * @typedef {{
 *   ver: 2,
 *   styleObject?: object | null,
 *   displaySpanInSeconds: number,
 *   startDayDisplayTime?: string | null,
 *   endDayDisplayTime?: string | null,
 *   rowCount: number,
 *   currentDisplayedStartDate: string | null,
 *   rowsDisplayedStart: number,
 *   autoUpdate: boolean,
 *   extraColumns: object[],
 *   filterExclusionLists: {
 *     [filterName: string]: string[],
 *   },
 * }} CustomizedResGanttConfiguration
 */

const VIEW_NAME = "CustomizedResGantt"

/**
 * @type {CustomizedResGanttConfiguration}
 */
const defaultValue = {
  ver: 2,
  styleObject: null,
  displaySpanInSeconds: 60 * 60 * 24 * 7,  // "1Week"
  startDayDisplayTime: "00:00",
  endDayDisplayTime: "23:59",
  rowCount: 5,
  rowSearchPattern: null,
  currentDisplayedStartDate: null,
  rowsDisplayedStart: 0,
  autoUpdate: true,
  extraColumns: [
    {
      name: "シフトの区切り時刻", 
      nameTranslationKey: "$t(asprova.terms.shiftDemarcationTime)",
      code: "Res_MyScheduleSeparateTimeForCustomizedResGantt",
      expectedLength: 8,
      displayed: true
    },
  ],
  filterExclusionLists: {}
}

const CustomizedResGanttConfigurationSchema = z.object({
  ver: z.literal(2),
  styleObject: z.union([
    z.object({
      StyleName: z.string(),
      ObjectID: z.number().int(),
    }),
    z.null(),
  ]),
  displaySpanInSeconds: z.number().int().min(1),
  startDayDisplayTime: z.union([
    z.string(),
    z.null(),
  ]),
  endDayDisplayTime: z.union([
    z.string(),
    z.null(),
  ]),
  rowCount: z.number().int().min(1),
  currentDisplayedStartDate: z.union([
    z.string(),
    z.null(),
  ]),
  rowsDisplayedStart: z.number(),
  extraColumns: z.array(z.any()),
  autoUpdate: z.boolean(),
  filterExclusionLists: z.record(z.array(z.string())),
})

// THIS IS SANITY CHECK, DO NOT REMOVE THIS PLEASE
CustomizedResGanttConfigurationSchema.parse(defaultValue)

/**
 * @returns {CustomizedResGanttConfiguration}
 */
export function getCustomizedResGanttConfiguration(userId, projectId) {
  const conf = getConfiguration(VIEW_NAME, userId, projectId, defaultValue)
  try {
    return CustomizedResGanttConfigurationSchema.parse(conf)
  } catch (e) {
    console.log(e)
    console.error("Malformed configuration for CustomizedResGantt")
    return defaultValue
  }
}

/**
 * @param {Partial<CustomizedResGanttConfiguration>} configuration
 * @param {string} userId
 * @param {string} projectId
 */
export function saveCustomizedResGanttConfiguration(configuration, userId, projectId) {
  const saved = {...defaultValue, ...getCustomizedResGanttConfiguration(userId, projectId), ...configuration}
  try {
    const parsed = CustomizedResGanttConfigurationSchema.parse(saved)
    saveConfiguration(VIEW_NAME, userId, projectId, parsed)
  } catch (e) {
    console.log(e)
    console.error("Malformed configuration for CustomizedResGantt")
  }
}

export async function connectCustomizedResGanttConfigurationToGanttStore(GanttStore, userId, projectId) {
  await applyConfigurationToGanttStore(GanttStore, getCustomizedResGanttConfiguration(userId, projectId))

  await subscribeGanttStoreWithConfiguration(GanttStore, conf => saveCustomizedResGanttConfiguration(conf, userId, projectId))
}
